import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * Error boundary that catches React rendering crashes and shows a recovery UI
 * instead of a blank white page. Logs the actual error for debugging.
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('[ErrorBoundary] React rendering crash caught:', error);
        console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="h-screen flex items-center justify-center bg-bg-warm">
                    <div className="text-center max-w-md px-6">
                        <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-danger-bg flex items-center justify-center">
                            <svg className="w-6 h-6 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-semibold text-fg mb-2">Something went wrong</h2>
                        <p className="text-sm text-fg-tertiary mb-4">
                            A rendering error occurred. Check the browser console for details.
                        </p>
                        {this.state.error && (
                            <p className="text-xs text-danger bg-danger-bg rounded px-3 py-2 mb-4 font-mono break-all">
                                {this.state.error.message}
                            </p>
                        )}
                        <button
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-accent text-white text-sm rounded-md hover:bg-accent-hover transition-colors"
                        >
            Reload page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
